/**
 * verdictSchemas.js — per-category structured fields for the verdict form.
 * BR-011 Tranche 2.3
 *
 * Each category gets a small list of structured fields that the expert
 * fills in alongside the free-form verdict_text. We don't store these as
 * separate columns yet — at submit time we fold them into a structured
 * Markdown prefix on `verdict_text`, so the persisted verdict is still a
 * single string but reads as if it were a well-structured assessment.
 *
 * Field types:
 *   - "radio"     → small set of mutually exclusive options
 *   - "list"      → dynamic list of free-text items
 *   - "textarea"  → free-form longer-form text
 *   - "boolean"   → yes/no toggle
 *
 * Keep schemas SMALL (2-4 fields each). The free-form verdict_text remains
 * the primary surface — this is structured prompting, not a replacement.
 */

export const VERDICT_SCHEMAS = {
  law: {
    label: "Legal review",
    fields: [
      {
        key: "risk_level",
        type: "radio",
        label: "Overall risk level",
        options: [
          { value: "low",      label: "Low" },
          { value: "medium",   label: "Medium" },
          { value: "high",     label: "High" },
          { value: "critical", label: "Critical" },
        ],
      },
      {
        key: "redline_recommended",
        type: "boolean",
        label: "Redline recommended before signing?",
      },
      {
        key: "clauses_to_negotiate",
        type: "list",
        label: "Clauses to renegotiate",
        placeholder: "e.g. clause 5.2 — unlimited liability exposure",
      },
    ],
  },

  product: {
    label: "Product review",
    fields: [
      {
        key: "scope_clarity",
        type: "radio",
        label: "Is the scope clearly defined?",
        options: [
          { value: "clear",      label: "Clear" },
          { value: "ambiguous",  label: "Ambiguous" },
          { value: "missing",    label: "Missing" },
        ],
      },
      {
        key: "missing_user_stories",
        type: "list",
        label: "Missing user stories or edge cases",
        placeholder: "e.g. what happens when offline?",
      },
    ],
  },

  engineering: {
    label: "Engineering review",
    fields: [
      {
        key: "scalability_concerns",
        type: "textarea",
        label: "Scalability concerns",
        placeholder: "What breaks at 10× current load?",
      },
      {
        key: "security_findings",
        type: "list",
        label: "Security findings",
        placeholder: "e.g. SQL injection risk in /api/search",
      },
    ],
  },

  finance: {
    label: "Finance review",
    fields: [
      {
        key: "assumption_validity",
        type: "radio",
        label: "Are the underlying assumptions defensible?",
        options: [
          { value: "yes",     label: "Yes" },
          { value: "partial", label: "Partially" },
          { value: "no",      label: "No" },
        ],
      },
      {
        key: "model_errors",
        type: "list",
        label: "Errors found in the model",
        placeholder: "e.g. revenue growth assumes 100% conversion",
      },
    ],
  },

  fundraising: {
    label: "Fundraising review",
    fields: [
      {
        key: "narrative_strength",
        type: "radio",
        label: "How strong is the narrative?",
        options: [
          { value: "compelling", label: "Compelling" },
          { value: "decent",     label: "Decent" },
          { value: "weak",       label: "Weak" },
        ],
      },
      {
        key: "red_flags",
        type: "list",
        label: "Red flags a VC would catch",
        placeholder: "e.g. no customer logos in a Series B deck",
      },
    ],
  },

  accounting: {
    label: "Accounting & Bookkeeping review",
    fields: [
      {
        key: "categorization_errors",
        type: "list",
        label: "Categorization errors found",
        placeholder: "e.g. SaaS subscriptions booked as office expenses",
      },
      {
        key: "compliance_concerns",
        type: "textarea",
        label: "Compliance concerns",
        placeholder: "Anything that could trigger an audit?",
      },
    ],
  },

  strategy: {
    label: "Strategy review",
    fields: [
      {
        key: "competitive_blind_spots",
        type: "list",
        label: "Competitive blind spots",
        placeholder: "e.g. ignoring open-source alternative",
      },
      {
        key: "execution_feasibility",
        type: "radio",
        label: "Execution feasibility",
        options: [
          { value: "feasible",   label: "Feasible" },
          { value: "stretched",  label: "Stretched" },
          { value: "unrealistic", label: "Unrealistic" },
        ],
      },
    ],
  },

  code_review: {
    label: "Code review",
    fields: [
      {
        key: "security_issues",
        type: "list",
        label: "Security issues",
        placeholder: "e.g. unsanitised user input in handleSearch()",
      },
      {
        key: "test_coverage_gaps",
        type: "list",
        label: "Test coverage gaps",
        placeholder: "e.g. no tests around payment retries",
      },
      {
        key: "refactor_recommendations",
        type: "list",
        label: "Refactor recommendations",
        placeholder: "e.g. extract auth middleware out of route handlers",
      },
    ],
  },

  ship_app: {
    label: "Ship readiness review",
    fields: [
      {
        key: "deployment_readiness",
        type: "radio",
        label: "Is this ready to ship?",
        options: [
          { value: "yes",   label: "Yes" },
          { value: "maybe", label: "Almost — fix below first" },
          { value: "no",    label: "No" },
        ],
      },
      {
        key: "blockers",
        type: "list",
        label: "Hard blockers",
        placeholder: "e.g. no error monitoring configured",
      },
      {
        key: "monitoring_gaps",
        type: "list",
        label: "Observability / monitoring gaps",
        placeholder: "e.g. no Sentry, no uptime alerts",
      },
    ],
  },
};

/**
 * Build a Markdown prefix from the schema answers. Returned string is
 * prepended to verdict_text on submit so the persisted verdict reads as
 * a well-structured assessment without requiring a schema column.
 *
 * Returns "" if nothing was answered.
 */
export function buildSchemaPrefix(category, answers) {
  const schema = VERDICT_SCHEMAS[category];
  if (!schema || !answers) return "";

  const lines = [];
  for (const field of schema.fields) {
    const val = answers[field.key];
    if (val === undefined || val === null || val === "" ||
        (Array.isArray(val) && val.filter((v) => v && v.trim()).length === 0)) {
      continue;
    }
    if (field.type === "radio") {
      const opt = field.options.find((o) => o.value === val);
      lines.push(`**${field.label}:** ${opt ? opt.label : val}`);
    } else if (field.type === "boolean") {
      lines.push(`**${field.label}:** ${val ? "Yes" : "No"}`);
    } else if (field.type === "list") {
      const clean = (val || []).filter((v) => v && v.trim());
      if (clean.length === 0) continue;
      lines.push(`**${field.label}:**`);
      for (const item of clean) lines.push(`- ${item.trim()}`);
    } else if (field.type === "textarea") {
      lines.push(`**${field.label}:**\n${val.trim()}`);
    }
    lines.push("");
  }

  if (lines.length === 0) return "";
  return lines.join("\n") + "\n---\n\n";
}
